import React, { useCallback } from 'react';
import { Calculator, Grid, Ruler } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров плитки
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущей площади помещения
  const roomArea = (parseFloat(formData.roomWidth) || 0) * (parseFloat(formData.roomLength) || 0) 
    + (parseFloat(formData.additionalArea) || 0);
  
  // Обработчик изменения размеров помещения
  const handleRoomSizeChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Обработчик изменения размеров плитки
  const handleTileSizeChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newState = { ...prev, [name]: value };
      
      // Автоматически обновляем площадь упаковки при изменении размеров плитки
      if (name === 'tileWidth' || name === 'tileLength') {
        const tileWidth = parseFloat(name === 'tileWidth' ? value : prev.tileWidth) || 0;
        const tileLength = parseFloat(name === 'tileLength' ? value : prev.tileLength) || 0;
        const tilesInPackage = parseFloat(prev.tilesInPackage) || 0;
        
        if (tileWidth > 0 && tileLength > 0 && tilesInPackage > 0) {
          // Преобразуем см в м²
          const tileAreaInM2 = (tileWidth * tileLength) / 10000;
          const packageArea = tileAreaInM2 * tilesInPackage;
          newState.packageArea = packageArea.toFixed(2);
        }
      }
      
      return newState;
    });
  };
  
  // Функция расчета
  const calculateTile = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные размеры помещений для быстрого выбора
  const predefinedRooms = [
    { width: '3', length: '4', label: 'Спальня (12 м²)' },
    { width: '3', length: '3', label: 'Детская (9 м²)' },
    { width: '4', length: '5', label: 'Гостиная (20 м²)' },
    { width: '2.5', length: '2', label: 'Ванная (5 м²)' },
  ];
  
  // Предопределенные размеры плитки для быстрого выбора
  const predefinedTiles = [
    { width: '20', length: '20', label: '20×20 см' },
    { width: '30', length: '30', label: '30×30 см' },
    { width: '40', length: '40', label: '40×40 см' },
    { width: '60', length: '60', label: '60×60 см' },
    { width: '15', length: '60', label: '15×60 см' },
    { width: '20', length: '60', label: '20×60 см' },
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-fuchsia-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Размеры помещения */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Размеры помещения</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(roomArea, 1)} м²</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label htmlFor="quickRoomWidth" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Ширина (м)
              </label>
              <input
                type="text"
                id="quickRoomWidth"
                name="roomWidth"
                value={formData.roomWidth}
                onChange={handleRoomSizeChange}
                className="form-input py-1 text-sm"
              />
            </div>
            <div>
              <label htmlFor="quickRoomLength" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Длина (м)
              </label>
              <input
                type="text"
                id="quickRoomLength"
                name="roomLength"
                value={formData.roomLength}
                onChange={handleRoomSizeChange}
                className="form-input py-1 text-sm"
              />
            </div>
          </div>
          
          {/* Кнопки с предопределенными размерами помещений */}
          <div className="grid grid-cols-2 gap-1 mt-2">
            {predefinedRooms.map(room => (
              <button
                key={room.label}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  roomWidth: room.width, 
                  roomLength: room.length 
                }))}
                className={`px-2 py-1.5 text-xs rounded border transition-colors
                  ${formData.roomWidth === room.width && formData.roomLength === room.length
                    ? 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:border-fuchsia-700 dark:text-fuchsia-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {room.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Размеры плитки */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Размеры плитки</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.tileWidth}×{formData.tileLength} см
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label htmlFor="quickTileWidth" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Ширина (см)
              </label>
              <input
                type="text"
                id="quickTileWidth"
                name="tileWidth"
                value={formData.tileWidth}
                onChange={handleTileSizeChange}
                className="form-input py-1 text-sm"
              />
            </div>
            <div>
              <label htmlFor="quickTileLength" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Длина (см)
              </label>
              <input
                type="text"
                id="quickTileLength"
                name="tileLength"
                value={formData.tileLength}
                onChange={handleTileSizeChange}
                className="form-input py-1 text-sm"
              />
            </div>
          </div>
          
          {/* Кнопки с предопределенными размерами плитки */}
          <div className="grid grid-cols-3 gap-1 mt-2">
            {predefinedTiles.map(tile => (
              <button
                key={tile.label}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  tileWidth: tile.width, 
                  tileLength: tile.length 
                }))}
                className={`px-2 py-1.5 text-xs rounded border transition-colors
                  ${formData.tileWidth === tile.width && formData.tileLength === tile.length
                    ? 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:border-fuchsia-700 dark:text-fuchsia-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {tile.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип плитки */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип плитки</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, tileType: 'ceramic' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.tileType === 'ceramic' 
                  ? 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:border-fuchsia-700 dark:text-fuchsia-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Керамическая</div>
              <div className="text-xs opacity-75">Классическая</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, tileType: 'porcelain' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.tileType === 'porcelain' 
                  ? 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:border-fuchsia-700 dark:text-fuchsia-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Керамогранит</div>
              <div className="text-xs opacity-75">Долговечная</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTile}
          icon={<Grid className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать плитку
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;